/**
 * Pure transforms from DB `League.standings` JSON + teams to the normalized StandingRow shape.
 * Form is normalized to the last 5 letters in the order the API provides them.
 */

import type {
  LeagueStandingsScope,
  NormalizedStandings,
  RawLeagueStandingsPayload,
  RawStandingRow,
  RawLeagueTeam,
  StandingFormLetter,
  StandingRow,
  StandingStatsSlice,
} from '../types/leagueDetail.types';
import { AGGREGATE_GROUP_NAME, UNKNOWN_TEAM_LABEL } from '../constants/leagueDetail.constants';

const MAX_FORM_LETTERS = 5;

/**
 * API-Football returns a non-group "Group Stage" aggregate alongside the real
 * per-group tables (and nested "Group Stage - Group A" keys). It is not a real
 * standings group, so single-table consumers (groups[0]) must not land on it.
 */
export function isAggregateGroupName(name: string): boolean {
  return name.trim().toLowerCase() === AGGREGATE_GROUP_NAME;
}

/** Display label for a standings group: drop a leading "Group Stage - " nesting prefix. */
export function formatStandingsGroupName(name: string): string {
  const sep = name.lastIndexOf(' - ');
  return sep >= 0 ? name.slice(sep + 3).trim() : name.trim();
}

function parseFormLetters(raw: string | null | undefined): StandingFormLetter[] {
  if (typeof raw !== 'string' || raw.length === 0) return [];
  const upper = raw.toUpperCase();
  const letters: StandingFormLetter[] = [];
  for (const char of upper) {
    if (char === 'W' || char === 'D' || char === 'L') {
      letters.push(char);
    }
  }
  return letters.slice(-MAX_FORM_LETTERS);
}

function pickDisplayName(team: RawLeagueTeam | undefined): string {
  if (!team) return UNKNOWN_TEAM_LABEL;
  const name = team.name?.trim();
  if (name && name.length > 0) return name;
  const short = team.shortName?.trim();
  if (short && short.length > 0) return short;
  return UNKNOWN_TEAM_LABEL;
}

function pickDisplayNameFromSources(
  team: RawLeagueTeam | undefined,
  raw: RawStandingRow
): string {
  if (team) return pickDisplayName(team);
  const rawRecord = raw as unknown as Record<string, unknown>;
  const sourceName =
    (typeof raw.sourceTeamName === 'string' ? raw.sourceTeamName : null) ??
    (typeof rawRecord.teamName === 'string' ? rawRecord.teamName : null) ??
    (typeof rawRecord.name === 'string' ? rawRecord.name : null);
  const normalizedSourceName = sourceName?.trim();
  if (normalizedSourceName && normalizedSourceName.length > 0) return normalizedSourceName;
  const sourceShort =
    (typeof raw.sourceTeamShortName === 'string' ? raw.sourceTeamShortName : null) ??
    (typeof rawRecord.teamShortName === 'string' ? rawRecord.teamShortName : null) ??
    (typeof rawRecord.teamCode === 'string' ? rawRecord.teamCode : null);
  const normalizedSourceShort = sourceShort?.trim();
  if (normalizedSourceShort && normalizedSourceShort.length > 0) return normalizedSourceShort;
  return UNKNOWN_TEAM_LABEL;
}

function buildStandingRow(
  raw: RawStandingRow,
  stats: {
    rank: number;
    played: number;
    won: number;
    drawn: number;
    lost: number;
    points: number;
    goalsFor: number;
    goalsAgainst: number;
  },
  teamsById: Map<string, RawLeagueTeam>,
  teamsByExternalId: Map<number, RawLeagueTeam>
): StandingRow | null {
  if (!raw || typeof raw.teamId !== 'string') return null;
  const team = teamsById.get(raw.teamId) ?? teamsByExternalId.get(raw.teamExternalId);
  const displayName = pickDisplayNameFromSources(team, raw);
  const teamName = team?.name ?? displayName;
  const goalDiff = stats.goalsFor - stats.goalsAgainst;
  return {
    rank: stats.rank,
    teamId: raw.teamId,
    teamExternalId: typeof raw.teamExternalId === 'number' ? raw.teamExternalId : null,
    teamName,
    displayName,
    kitPrimary: team?.kitPrimary ?? null,
    kitSecondary: team?.kitSecondary ?? null,
    kitType: team?.kitType ?? null,
    countryCode: team?.countryCode ?? null,
    played: stats.played,
    won: stats.won,
    drawn: stats.drawn,
    lost: stats.lost,
    goalsFor: stats.goalsFor,
    goalsAgainst: stats.goalsAgainst,
    goalDiff,
    points: stats.points,
    form: parseFormLetters(raw.form),
  };
}

function toOverallStandingRow(
  raw: RawStandingRow,
  teamsById: Map<string, RawLeagueTeam>,
  teamsByExternalId: Map<number, RawLeagueTeam>
): StandingRow | null {
  return buildStandingRow(
    raw,
    {
      rank: raw.position,
      played: raw.played,
      won: raw.won,
      drawn: raw.drawn,
      lost: raw.lost,
      points: raw.points,
      goalsFor: raw.goalsFor,
      goalsAgainst: raw.goalsAgainst,
    },
    teamsById,
    teamsByExternalId
  );
}

function compareStandingsRows(a: StandingRow, b: StandingRow): number {
  if (b.points !== a.points) return b.points - a.points;
  if (b.goalDiff !== a.goalDiff) return b.goalDiff - a.goalDiff;
  if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
  return a.displayName.localeCompare(b.displayName);
}

function mapGroupRowsForScope(
  rows: RawStandingRow[],
  teamsById: Map<string, RawLeagueTeam>,
  teamsByExternalId: Map<number, RawLeagueTeam>,
  scope: LeagueStandingsScope
): StandingRow[] {
  if (scope === 'all') {
    return rows
      .map((row) => toOverallStandingRow(row, teamsById, teamsByExternalId))
      .filter((row): row is StandingRow => row !== null)
      .sort((a, b) => a.rank - b.rank);
  }

  const sliceKey: keyof Pick<RawStandingRow, 'home' | 'away'> =
    scope === 'home' ? 'home' : 'away';
  const interim: StandingRow[] = [];
  for (const raw of rows) {
    const slice: StandingStatsSlice | undefined = raw[sliceKey];
    if (!slice) continue;
    const row = buildStandingRow(
      raw,
      {
        rank: 0,
        played: slice.played,
        won: slice.won,
        drawn: slice.drawn,
        lost: slice.lost,
        points: slice.points,
        goalsFor: slice.goalsFor,
        goalsAgainst: slice.goalsAgainst,
      },
      teamsById,
      teamsByExternalId
    );
    if (row) interim.push(row);
  }
  return interim.sort(compareStandingsRows).map((row, index) => ({
    ...row,
    rank: index + 1,
  }));
}

/** True when stored standings include API-Football home/away blocks (run league standings sync to backfill). */
export function leagueStandingsHasHomeAwaySplit(payload: RawLeagueStandingsPayload | null): boolean {
  if (!payload?.standings || typeof payload.standings !== 'object' || Array.isArray(payload.standings)) {
    return false;
  }
  const firstGroup = Object.values(payload.standings)[0];
  if (!Array.isArray(firstGroup) || firstGroup.length === 0) return false;
  const row = firstGroup[0];
  return row != null && row.home != null && row.away != null;
}

export function normalizeStandingsPayload(
  payload: RawLeagueStandingsPayload | null,
  scope: LeagueStandingsScope = 'all'
): NormalizedStandings | null {
  if (!payload) return null;

  const teamsById = new Map(payload.teams.map((t) => [t.id, t]));
  const teamsByExternalId = new Map(
    payload.teams
      .filter((t) => typeof t.externalId === 'number')
      .map((t) => [t.externalId as number, t])
  );
  const groupsInput = payload.standings ?? {};
  const entries = Object.entries(groupsInput);

  const groups = entries
    .map(([name, rows]) => {
      const mapped = mapGroupRowsForScope(rows, teamsById, teamsByExternalId, scope);
      return { name, rows: mapped };
    })
    .filter((group) => group.rows.length > 0);

  if (groups.length === 0) return null;

  // Keep the "Group Stage" aggregate but push it last, so consumers reading the
  // first group (or every group in order) land on real groups first and only
  // see the aggregate at the bottom.
  const ordered = [
    ...groups.filter((group) => !isAggregateGroupName(group.name)),
    ...groups.filter((group) => isAggregateGroupName(group.name)),
  ];

  return {
    league: payload.league,
    groups: ordered,
  };
}
